import { supabase } from '../lib/supabase'
import { userLogsService } from './userLogs'
import {
  SuperappUser,
  SuperappUserBehavior,
  SuperappUserBehaviorStatus,
  SuperappUserListStatus,
  BehaviorDataUser,
  DashboardMetrics,
  SuperappUserFilters,
  SuperappBehaviorFilters,
  SuperappPromotion,
  SuperappPromotionFilters,
  SuperappAppIndicationRef,
  SuperappDashboardMilestone,
  SuperappDashboardMilestonePayload,
} from '../types/superapp'

const SUPABASE_PAGE_SIZE = 1000

function milestoneLogSnapshot(milestone: Pick<SuperappDashboardMilestone, 'name' | 'event_date'>) {
  return {
    name: milestone.name,
    event_date: milestone.event_date,
  }
}

async function logMilestoneCreate(milestone: SuperappDashboardMilestone): Promise<void> {
  try {
    await userLogsService.logCreate(
      'superapp_dashboard_milestone',
      milestone.id,
      `Criou momento-chave "${milestone.name}" (${milestone.event_date}) no Superapp Dashboard`,
      { new_values: milestoneLogSnapshot(milestone) }
    )
  } catch (logError) {
    console.warn('Erro ao registrar log (momento-chave — criar):', logError)
  }
}

async function logMilestoneUpdate(
  previous: Pick<SuperappDashboardMilestone, 'name' | 'event_date'>,
  milestone: SuperappDashboardMilestone
): Promise<void> {
  try {
    await userLogsService.logUpdate(
      'superapp_dashboard_milestone',
      milestone.id,
      `Atualizou momento-chave "${milestone.name}" (${milestone.event_date}) no Superapp Dashboard`,
      milestoneLogSnapshot(previous),
      milestoneLogSnapshot(milestone)
    )
  } catch (logError) {
    console.warn('Erro ao registrar log (momento-chave — atualizar):', logError)
  }
}

async function logMilestoneDelete(milestone: SuperappDashboardMilestone): Promise<void> {
  try {
    await userLogsService.logDelete(
      'superapp_dashboard_milestone',
      milestone.id,
      `Excluiu momento-chave "${milestone.name}" (${milestone.event_date}) do Superapp Dashboard`,
      { old_values: milestoneLogSnapshot(milestone) }
    )
  } catch (logError) {
    console.warn('Erro ao registrar log (momento-chave — excluir):', logError)
  }
}

export function normalizeCpfDigits(cpf: string | null | undefined): string | null {
  if (!cpf) return null
  const digits = cpf.replace(/\D/g, '')
  return digits.length > 0 ? digits : null
}

async function fetchAllPages<T>(buildQuery: (from: number, to: number) => any): Promise<T[]> {
  const all: T[] = []
  let from = 0
  let hasMore = true

  while (hasMore) {
    const to = from + SUPABASE_PAGE_SIZE - 1
    const { data, error } = await buildQuery(from, to)
    if (error) throw error

    const page = (data || []) as T[]
    all.push(...page)

    if (page.length < SUPABASE_PAGE_SIZE) {
      hasMore = false
    } else {
      from += SUPABASE_PAGE_SIZE
    }
  }

  return all
}

/** `behavior_data` (jsonb) na tabela é um objeto por linha; formato legado com array ainda é aceito. */
export function behaviorDataToSnapshots(raw: unknown): BehaviorDataUser[] {
  if (raw == null) return []
  if (Array.isArray(raw)) {
    return raw.filter((x) => x != null && typeof x === 'object') as BehaviorDataUser[]
  }
  if (typeof raw === 'object') return [raw as BehaviorDataUser]
  return []
}

export function parseBehaviorDataSnapshot(raw: unknown): BehaviorDataUser | null {
  const list = behaviorDataToSnapshots(raw)
  return list[0] ?? null
}

/** E-mail ou telefone preenchido em superapp_users. */
export function hasSuperappContactInfo(user: {
  email?: string | null
  phone?: string | null
}): boolean {
  return Boolean(user.email?.trim() || user.phone?.trim())
}

/** Classifica o status exibido com base no último snapshot válido e no cadastro. */
export function resolveSuperappUserListStatus(
  user: { email?: string | null; phone?: string | null },
  behavior: SuperappUserBehaviorStatus | undefined
): SuperappUserListStatus {
  if (behavior) {
    return behavior.is_active ? 'ativo' : 'inativo'
  }
  if (hasSuperappContactInfo(user)) return 'cadastro_parcial'
  return 'sem_dados'
}

export function getSuperappUserListStatusLabel(status: SuperappUserListStatus): string {
  switch (status) {
    case 'ativo':
      return 'Ativo'
    case 'inativo':
      return 'Inativo'
    case 'cadastro_parcial':
      return 'Cadastro Parcial'
    case 'sem_dados':
      return 'Sem dados'
  }
}

function extractCityFromSnapshot(snapshot: unknown): string | null {
  if (snapshot == null || typeof snapshot !== 'object') return null
  const data = snapshot as Record<string, unknown>
  const loc = data.localizacao
  const candidates: unknown[] = []
  if (loc != null && typeof loc === 'object') {
    const locObj = loc as Record<string, unknown>
    candidates.push(locObj.city, locObj.cidade, locObj.locality, locObj.municipio)
  }
  candidates.push(data.city, data.cidade)
  const localizacaoAccent = data.localização
  if (localizacaoAccent != null && typeof localizacaoAccent === 'object') {
    const locObj = localizacaoAccent as Record<string, unknown>
    candidates.push(locObj.city, locObj.cidade)
  }
  for (const candidate of candidates) {
    if (candidate == null) continue
    const label = String(candidate).trim().replace(/\s+/g, ' ')
    if (label) return label
  }
  return null
}

/** Chave estável para agrupar grafias equivalentes (acentos, UF, caixa). */
export function normalizeCityGroupingKey(city: string): string {
  return city
    .trim()
    .replace(/\s+/g, ' ')
    .toLocaleLowerCase('pt-BR')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/,\s*[a-z]{2}$/i, '')
    .replace(/\s*-\s*[a-z]{2}$/i, '')
    .trim()
}

/** Data de lançamento do Superapp (fuso America/Sao_Paulo, YYYY-MM-DD). */
export const SUPERAPP_LAUNCH_DATE = '2026-05-13'

export const MILESTONE_CHART_COLORS = [
  '#008B95',
  '#9333ea',
  '#d97706',
  '#dc2626',
  '#059669',
  '#6366f1',
  '#000000',
] as const

export type SuperappChartMarker = {
  id: string
  date: string
  legendLabel: string
  color: string
}

export function milestonesToChartMarkers(
  milestones: SuperappDashboardMilestone[]
): SuperappChartMarker[] {
  return milestones.map((m, index) => ({
    id: m.id,
    date: m.event_date,
    legendLabel: m.name,
    color: MILESTONE_CHART_COLORS[index % MILESTONE_CHART_COLORS.length],
  }))
}

/** Data YYYY-MM-DD no fuso America/Sao_Paulo. */
export function getDateInSPTimezone(date: Date): string {
  const dateSP = new Date(date.toLocaleString('en-US', { timeZone: 'America/Sao_Paulo' }))
  const year = dateSP.getFullYear()
  const month = String(dateSP.getMonth() + 1).padStart(2, '0')
  const day = String(dateSP.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/** Dias corridos desde o lançamento (13/05/2026 inclusive até a data de referência, fuso SP). */
export function getDaysSinceSuperappLaunchInclusive(referenceDate = new Date()): number {
  const todaySP = getDateInSPTimezone(referenceDate)
  if (todaySP < SUPERAPP_LAUNCH_DATE) return 0
  const [y1, m1, d1] = SUPERAPP_LAUNCH_DATE.split('-').map(Number)
  const [y2, m2, d2] = todaySP.split('-').map(Number)
  const launch = new Date(y1, m1 - 1, d1)
  const today = new Date(y2, m2 - 1, d2)
  return Math.floor((today.getTime() - launch.getTime()) / 86_400_000) + 1
}

export type SuperappDailyMetricRow = {
  date: string
  count: number
  ativo: number
  inativo: number
  semDados: number
  cadastroParcial: number
}

/** Lista YYYY-MM-DD consecutivos de start até end (inclusive). */
export function enumerateDateRangeYmd(start: string, end: string): string[] {
  const [y1, m1, d1] = start.split('-').map(Number)
  const [y2, m2, d2] = end.split('-').map(Number)
  const cur = new Date(y1, m1 - 1, d1)
  const endDate = new Date(y2, m2 - 1, d2)
  const dates: string[] = []
  while (cur.getTime() <= endDate.getTime()) {
    const year = cur.getFullYear()
    const month = String(cur.getMonth() + 1).padStart(2, '0')
    const day = String(cur.getDate()).padStart(2, '0')
    dates.push(`${year}-${month}-${day}`)
    cur.setDate(cur.getDate() + 1)
  }
  return dates
}

/** Preenche dias sem registro com zeros no intervalo [startDate, endDate]. */
export function fillDailyMetricSeries(
  sparse: SuperappDailyMetricRow[],
  startDate: string,
  endDate: string
): SuperappDailyMetricRow[] {
  const byDate = new Map(sparse.map((r) => [r.date, r]))
  return enumerateDateRangeYmd(startDate, endDate).map((date) => {
    const row = byDate.get(date)
    if (row) return row
    return { date, ativo: 0, inativo: 0, semDados: 0, cadastroParcial: 0, count: 0 }
  })
}

/** Máximo de dias carregados nos gráficos diários do dashboard (filtro no cliente: 90/60/30). */
export const SUPERAPP_DASHBOARD_CHART_MAX_DAYS = 90

export const SUPERAPP_DASHBOARD_CHART_DAY_OPTIONS = [90, 60, 30] as const
export type SuperappDashboardChartDays = (typeof SUPERAPP_DASHBOARD_CHART_DAY_OPTIONS)[number]

/** Data inicial (inclusive) dos últimos N dias corridos, fuso SP. */
export function getStartDateForLastNDaysInclusive(
  days: number,
  referenceDate = new Date()
): string {
  const [y, m, d] = getDateInSPTimezone(referenceDate).split('-').map(Number)
  const cur = new Date(y, m - 1, d)
  cur.setDate(cur.getDate() - (days - 1))
  const year = cur.getFullYear()
  const month = String(cur.getMonth() + 1).padStart(2, '0')
  const day = String(cur.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/** Restringe série diária preenchida aos últimos N dias (inclusive hoje, fuso SP). */
export function filterDailyMetricSeriesByLastDays(
  series: SuperappDailyMetricRow[],
  days: number,
  referenceDate = new Date()
): SuperappDailyMetricRow[] {
  const endDate = getDateInSPTimezone(referenceDate)
  const startDate = getStartDateForLastNDaysInclusive(days, referenceDate)
  return series.filter((r) => r.date >= startDate && r.date <= endDate)
}

// =====================================================
// SERVIÇOS DO SUPERAPP DASHBOARD
// =====================================================

export const superappService = {
  // Obter todos os usuários com filtros opcionais
  async getAllUsers(filters: SuperappUserFilters = {}): Promise<SuperappUser[]> {
    try {
      return await fetchAllPages<SuperappUser>((from, to) => {
        let query = supabase.from('superapp_users').select('*')

        if (filters.name) {
          query = query.ilike('name', `%${filters.name}%`)
        }
        if (filters.email) {
          query = query.ilike('email', `%${filters.email}%`)
        }
        if (filters.cpf) {
          query = query.ilike('cpf', `%${filters.cpf}%`)
        }
        if (filters.phone) {
          query = query.ilike('phone', `%${filters.phone}%`)
        }
        if (filters.company) {
          query = query.ilike('company', `%${filters.company}%`)
        }
        if (filters.created_from) {
          query = query.gte('created_at', new Date(`${filters.created_from}T00:00:00`).toISOString())
        }
        if (filters.created_to) {
          query = query.lte('created_at', new Date(`${filters.created_to}T23:59:59`).toISOString())
        }

        return query.order('created_at', { ascending: false }).range(from, to)
      })
    } catch (error) {
      console.error('Erro ao buscar usuários:', error)
      throw error
    }
  },

  // Obter usuário por ID
  async getUserById(userId: string): Promise<SuperappUser | null> {
    try {
      const { data, error } = await supabase
        .from('superapp_users')
        .select('*')
        .eq('user_id', userId)
        .single()

      if (error) throw error
      return data
    } catch (error) {
      console.error('Erro ao buscar usuário:', error)
      throw error
    }
  },

  // Mapa user_id -> { is_active, risk_churn } a partir do snapshot válido mais recente (para filtros na aba Usuários)
  async getUsersBehaviorStatusMap(): Promise<Record<string, SuperappUserBehaviorStatus>> {
    try {
      const behaviorsData = await fetchAllPages<{ user_id: string; behavior_data: unknown }>((from, to) =>
        supabase
          .from('superapp_user_behavior')
          .select('user_id, behavior_data')
          .order('created_at', { ascending: false })
          .range(from, to)
      )

      const map: Record<string, SuperappUserBehaviorStatus> = {}
      for (const row of behaviorsData) {
        const uid = row.user_id
        if (!uid || map[uid] !== undefined) continue
        const snap = parseBehaviorDataSnapshot(row.behavior_data)
        if (!snap) continue
        const comp = snap.comportamento
        map[uid] = {
          is_active: comp?.is_active ?? false,
          risk_churn: comp?.risk_churn ?? false
        }
      }
      return map
    } catch (error) {
      console.error('Erro ao buscar status de comportamento dos usuários:', error)
      return {}
    }
  },

  /** CPF normalizado (dígitos) → corretor que indicou no Super APP */
  async getAppIndicationByCpfMap(): Promise<Record<string, SuperappAppIndicationRef>> {
    try {
      const rows = await fetchAllPages<{
        cpf: string
        broker: { name: string | null; id_scgp: number | null } | null
      }>((from, to) =>
        supabase
          .from('dashcorretor_app_indications')
          .select('cpf, broker:brokers(name, id_scgp)')
          .order('cpf', { ascending: true })
          .range(from, to)
      )

      const map: Record<string, SuperappAppIndicationRef> = {}
      for (const row of rows) {
        const digits = normalizeCpfDigits(row.cpf)
        if (!digits || map[digits]) continue
        map[digits] = {
          broker_name: row.broker?.name?.trim() || '(Sem nome)',
          broker_code: row.broker?.id_scgp ?? null,
        }
      }
      return map
    } catch (error) {
      console.error('Erro ao buscar indicações Super APP por CPF:', error)
      return {}
    }
  },

  /** Contagem de acionamentos de assistências (middleware_partners) por CPF só-dígitos. */
  async getAssistanceActivationCountByCpfMap(): Promise<Record<string, number>> {
    try {
      const rows = await fetchAllPages<{ cpf: string | null }>((from, to) =>
        supabase
          .from('middleware_partners')
          .select('cpf')
          .order('id', { ascending: true })
          .range(from, to)
      )

      const map: Record<string, number> = {}
      for (const row of rows) {
        const digits = normalizeCpfDigits(row.cpf)
        if (!digits) continue
        map[digits] = (map[digits] ?? 0) + 1
      }
      return map
    } catch (error) {
      console.error('Erro ao contar acionamentos de assistências por CPF:', error)
      return {}
    }
  },

  // Obter contagem de comportamentos de um usuário
  async getUserBehaviorCount(userId: string): Promise<number> {
    try {
      const { count, error } = await supabase
        .from('superapp_user_behavior')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId)

      if (error) throw error
      return count || 0
    } catch (error) {
      console.error('Erro ao contar comportamentos:', error)
      return 0
    }
  },

  async getLatestUserBehaviorByUserId(userId: string): Promise<SuperappUserBehavior | null> {
    try {
      const { data, error } = await supabase
        .from('superapp_user_behavior')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle()

      if (error) throw error
      if (!data) return null

      return {
        ...data,
        user: null,
      }
    } catch (error) {
      console.error('Erro ao buscar comportamento do usuário:', error)
      throw error
    }
  },

  // Obter todos os comportamentos com filtros opcionais e relacionamento com usuários
  async getUserBehaviors(filters: SuperappBehaviorFilters = {}): Promise<SuperappUserBehavior[]> {
    try {
      const behaviorsData = await fetchAllPages<any>((from, to) =>
        supabase
          .from('superapp_user_behavior')
          .select('*')
          .order('created_at', { ascending: false })
          .range(from, to)
      )

      // Buscar usuários relacionados
      const userIds = Array.from(new Set(
        behaviorsData
          .map(b => b.user_id)
          .filter((id): id is string => id !== null && id !== undefined)
      ))

      let usersMap = new Map<string, SuperappUser>()
      if (userIds.length > 0) {
        const IN_CHUNK_SIZE = 200
        for (let i = 0; i < userIds.length; i += IN_CHUNK_SIZE) {
          const chunk = userIds.slice(i, i + IN_CHUNK_SIZE)
          const { data: usersData } = await supabase
            .from('superapp_users')
            .select('*')
            .in('user_id', chunk)

          if (usersData) {
            usersData.forEach(user => {
              usersMap.set(user.user_id, user)
            })
          }
        }
      }

      // Aplicar filtros de nome e email e anexar dados do usuário
      let filteredData = (behaviorsData || []).map((behavior: any) => ({
        ...behavior,
        user: behavior.user_id ? usersMap.get(behavior.user_id) || null : null
      }))

      if (filters.name || filters.email) {
        filteredData = filteredData.filter((behavior: any) => {
          // Primeiro tenta buscar do relacionamento com superapp_users
          if (behavior.user) {
            const nameMatch = !filters.name || (behavior.user.name?.toLowerCase().includes(filters.name!.toLowerCase()) ?? false)
            const emailMatch = !filters.email || (behavior.user.email?.toLowerCase().includes(filters.email!.toLowerCase()) ?? false)
            if (nameMatch && emailMatch) return true
          }

          // Se não encontrou no relacionamento, busca no JSON behavior_data
          const snaps = behaviorDataToSnapshots(behavior.behavior_data)
          if (snaps.length > 0) {
            const nameMatch =
              !filters.name ||
              snaps.some((u) => u.name?.toLowerCase().includes(filters.name!.toLowerCase()))
            const emailMatch =
              !filters.email ||
              snaps.some((u) => u.email?.toLowerCase().includes(filters.email!.toLowerCase()))

            return nameMatch && emailMatch
          }

          return false
        })
      }

      return filteredData
    } catch (error) {
      console.error('Erro ao buscar comportamentos:', error)
      throw error
    }
  },

  // Obter métricas do dashboard
  async getDashboardMetrics(): Promise<DashboardMetrics> {
    // PERFORMANCE: carrega usuários e comportamentos em memória com paginação.
    // Com crescimento forte da base, preferir views/materialized views ou RPC no Supabase para agregações.
    const hasLocation = (u: any): boolean => {
      if (extractCityFromSnapshot(u)) return true
      const loc = u?.localizacao
      if (!loc) return false
      if (loc.city?.trim()) return true
      const lat = loc.latitude
      const lng = loc.longitude
      if (typeof lat === 'number' && typeof lng === 'number' && (lat !== 0 || lng !== 0)) return true
      return false
    }

    try {
      const { count: totalUsers, error: usersError } = await supabase
        .from('superapp_users')
        .select('*', { count: 'exact', head: true })

      if (usersError) throw usersError

      const { count: totalBehaviors, error: behaviorsError } = await supabase
        .from('superapp_user_behavior')
        .select('*', { count: 'exact', head: true })

      if (behaviorsError) throw behaviorsError

      const usersRows = await fetchAllPages<{
        user_id: string
        created_at: string | null
        email: string | null
        phone: string | null
        name: string | null
        cpf: string | null
        company: string | null
      }>((from, to) =>
        supabase
          .from('superapp_users')
          .select('user_id, created_at, email, phone, name, cpf, company')
          .order('created_at', { ascending: false })
          .range(from, to)
      )

      const behaviorsRows = await fetchAllPages<{
        user_id: string | null
        behavior_data: unknown
        created_at: string
      }>((from, to) =>
        supabase
          .from('superapp_user_behavior')
          .select('user_id, behavior_data, created_at')
          .order('created_at', { ascending: false })
          .range(from, to)
      )

      const usersWithBehaviorRecords = new Set(
        behaviorsRows.map((r) => r.user_id).filter((id): id is string => Boolean(id))
      ).size

      // Último snapshot por user_id (registro mais recente por created_at; payload em behavior_data é um objeto)
      const latestByUserId = new Map<string, any>()
      for (const row of behaviorsRows) {
        const uid = row.user_id
        if (!uid || latestByUserId.has(uid)) continue
        const snap = parseBehaviorDataSnapshot(row.behavior_data)
        if (snap) latestByUserId.set(uid, snap)
      }

      const usersWithBehaviorSnapshot = latestByUserId.size

      let activeUsers = 0
      let atRiskUsers = 0
      let totalEngagementScore = 0
      let engagementScoreCount = 0
      let engagementLow = 0
      let engagementMedium = 0
      let engagementHigh = 0
      let sumDaysSince = 0
      let countDaysSince = 0
      let sumLifetime = 0
      let countLifetime = 0
      let ghostUsersCount = 0
      let preferredTimeSlotNullCount = 0
      let lifecycleNew = 0
      let lifecycleDeveloping = 0
      let lifecycleMature = 0

      const favoriteModulesMap = new Map<string, number>()
      const preferredTimeSlotsMap = new Map<string, number>()
      const usersByCityMap = new Map<string, { label: string; count: number }>()
      const lastActionMap = new Map<string, number>()

      const riskProfile = {
        atRiskZeroEngagement: 0,
        atRiskZeroEvents: 0,
        atRiskNoLocation: 0
      }

      const lifeEng: Record<string, { sum: number; count: number }> = {
        'Novos (≤30 dias)': { sum: 0, count: 0 },
        'Em desenvolvimento (31–90)': { sum: 0, count: 0 },
        'Maduros (90+ dias)': { sum: 0, count: 0 }
      }

      latestByUserId.forEach((u) => {
        const comp = u.comportamento
        const ativ = u.atividade

        if (comp?.is_active) activeUsers++

        if (comp?.risk_churn) {
          atRiskUsers++
          const score = comp.engagement_score
          if (score === 0 || score === null || score === undefined) riskProfile.atRiskZeroEngagement++
          if (typeof comp.total_events === 'number' && comp.total_events === 0) riskProfile.atRiskZeroEvents++
          if (!hasLocation(u)) riskProfile.atRiskNoLocation++
        }

        if (comp?.engagement_score !== undefined && comp?.engagement_score !== null) {
          const s = Number(comp.engagement_score)
          totalEngagementScore += s
          engagementScoreCount++
          if (s <= 33) engagementLow++
          else if (s <= 66) engagementMedium++
          else engagementHigh++
        }

        const dsa = ativ?.days_since_last_activity
        if (typeof dsa === 'number' && !Number.isNaN(dsa)) {
          sumDaysSince += dsa
          countDaysSince++
        }

        const ltd = ativ?.customer_lifetime_days
        if (typeof ltd === 'number' && !Number.isNaN(ltd)) {
          sumLifetime += ltd
          countLifetime++
          if (ltd <= 30) {
            lifecycleNew++
            const b = lifeEng['Novos (≤30 dias)']
            if (comp?.engagement_score != null) {
              b.sum += Number(comp.engagement_score)
              b.count++
            }
          } else if (ltd <= 90) {
            lifecycleDeveloping++
            const b = lifeEng['Em desenvolvimento (31–90)']
            if (comp?.engagement_score != null) {
              b.sum += Number(comp.engagement_score)
              b.count++
            }
          } else {
            lifecycleMature++
            const b = lifeEng['Maduros (90+ dias)']
            if (comp?.engagement_score != null) {
              b.sum += Number(comp.engagement_score)
              b.count++
            }
          }
        }

        if (typeof comp?.total_events === 'number' && comp.total_events === 0) ghostUsersCount++

        const slot = comp?.preferred_time_slot
        if (slot === null || slot === undefined || String(slot).trim() === '') {
          preferredTimeSlotNullCount++
        } else {
          preferredTimeSlotsMap.set(slot, (preferredTimeSlotsMap.get(slot) || 0) + 1)
        }

        if (comp?.favorite_module) {
          const mod = comp.favorite_module
          favoriteModulesMap.set(mod, (favoriteModulesMap.get(mod) || 0) + 1)
        }

        const city = extractCityFromSnapshot(u)
        if (city) {
          const groupingKey = normalizeCityGroupingKey(city)
          if (!groupingKey) return
          const existing = usersByCityMap.get(groupingKey)
          if (existing) {
            existing.count += 1
            if (city.length > existing.label.length) existing.label = city
          } else {
            usersByCityMap.set(groupingKey, { label: city, count: 1 })
          }
        }

        const la = comp?.last_action
        if (la && String(la).trim()) {
          const key = String(la).trim()
          lastActionMap.set(key, (lastActionMap.get(key) || 0) + 1)
        }

      })

      const usersWithoutBehaviorSnapshot = usersRows.filter((r) => !latestByUserId.has(r.user_id))
      const usersSemDadosCount = usersWithoutBehaviorSnapshot.filter((r) => !hasSuperappContactInfo(r)).length
      const usersCadastroParcialCount = usersWithoutBehaviorSnapshot.length - usersSemDadosCount
      const inactiveUsersWithSnapshot = Math.max(0, usersWithBehaviorSnapshot - activeUsers)

      const usersByStatus = [
        { status: 'Ativo', count: activeUsers },
        { status: 'Inativo', count: inactiveUsersWithSnapshot },
        { status: 'Sem dados', count: usersSemDadosCount },
        { status: 'Cadastro parcial', count: usersCadastroParcialCount }
      ]

      const avgEngagementScore = engagementScoreCount > 0 ? totalEngagementScore / engagementScoreCount : 0
      const churnRiskRatePercent =
        usersWithBehaviorSnapshot > 0 ? (atRiskUsers / usersWithBehaviorSnapshot) * 100 : 0
      const avgDaysSinceLastActivity = countDaysSince > 0 ? sumDaysSince / countDaysSince : 0
      const avgCustomerLifetimeDays = countLifetime > 0 ? sumLifetime / countLifetime : 0

      const usersByDateStatusMap = new Map<
        string,
        { ativo: number; inativo: number; semDados: number; cadastroParcial: number }
      >()
      const chartPeriodStartDate = getStartDateForLastNDaysInclusive(SUPERAPP_DASHBOARD_CHART_MAX_DAYS)
      let newUsersSinceLaunch20260513 = 0
      let usersSemDadosCadastroSinceLaunch20260513 = 0
      let usersCadastroParcialSinceLaunch20260513 = 0
      let activeUsersSinceLaunch20260513 = 0
      for (const user of usersRows) {
        if (!user.created_at) continue
        const createdSP = getDateInSPTimezone(new Date(user.created_at))
        if (createdSP >= SUPERAPP_LAUNCH_DATE) {
          newUsersSinceLaunch20260513++
          const snap = latestByUserId.get(user.user_id)
          if (!snap) {
            if (hasSuperappContactInfo(user)) usersCadastroParcialSinceLaunch20260513++
            else usersSemDadosCadastroSinceLaunch20260513++
          } else if (snap.comportamento?.is_active) {
            activeUsersSinceLaunch20260513++
          }
        }
      }

      const daysSinceLaunch = getDaysSinceSuperappLaunchInclusive()
      const avgNewUsersPerDaySinceLaunch20260513 =
        daysSinceLaunch > 0 ? newUsersSinceLaunch20260513 / daysSinceLaunch : 0

      for (const user of usersRows) {
        if (!user.created_at) continue
        const userDateSP = getDateInSPTimezone(new Date(user.created_at))
        if (userDateSP < chartPeriodStartDate) continue

        const cur = usersByDateStatusMap.get(userDateSP) || {
          ativo: 0,
          inativo: 0,
          semDados: 0,
          cadastroParcial: 0
        }
        const snap = latestByUserId.get(user.user_id)
        if (!snap) {
          if (hasSuperappContactInfo(user)) cur.cadastroParcial++
          else cur.semDados++
        } else if (snap.comportamento?.is_active) cur.ativo++
        else cur.inativo++
        usersByDateStatusMap.set(userDateSP, cur)
      }

      const todaySP = getDateInSPTimezone(new Date())
      const behaviorsByDate = fillDailyMetricSeries(
        Array.from(usersByDateStatusMap.entries()).map(([date, v]) => ({
          date,
          ativo: v.ativo,
          inativo: v.inativo,
          semDados: v.semDados,
          cadastroParcial: v.cadastroParcial,
          count: v.ativo + v.inativo + v.semDados + v.cadastroParcial
        })),
        chartPeriodStartDate,
        todaySP
      )

      const usersByLastAccessDateMap = new Map<string, number>()
      for (const user of usersRows) {
        const snap = latestByUserId.get(user.user_id)
        const lastSeen = snap?.atividade?.last_seen_at
        if (!lastSeen || !snap) continue
        if (!snap.comportamento?.is_active) continue
        const accessDateSP = getDateInSPTimezone(new Date(lastSeen))
        if (accessDateSP < chartPeriodStartDate) continue

        usersByLastAccessDateMap.set(
          accessDateSP,
          (usersByLastAccessDateMap.get(accessDateSP) || 0) + 1
        )
      }

      const lastAccessByDate = fillDailyMetricSeries(
        Array.from(usersByLastAccessDateMap.entries()).map(([date, count]) => ({
          date,
          ativo: count,
          inativo: 0,
          semDados: 0,
          cadastroParcial: 0,
          count
        })),
        chartPeriodStartDate,
        todaySP
      )

      const partnerActivationRows = await fetchAllPages<{ created_at: string | null }>((from, to) =>
        supabase
          .from('middleware_partners')
          .select('created_at')
          .order('created_at', { ascending: true })
          .range(from, to)
      )

      const activationsByDateMap = new Map<string, number>()
      for (const row of partnerActivationRows) {
        if (!row.created_at) continue
        const dateSP = getDateInSPTimezone(new Date(row.created_at))
        if (dateSP < chartPeriodStartDate) continue
        activationsByDateMap.set(dateSP, (activationsByDateMap.get(dateSP) || 0) + 1)
      }

      const assistanceActivationsByDate = fillDailyMetricSeries(
        Array.from(activationsByDateMap.entries()).map(([date, count]) => ({
          date,
          count,
          ativo: 0,
          inativo: 0,
          semDados: 0,
          cadastroParcial: 0
        })),
        chartPeriodStartDate,
        todaySP
      ).map(({ date, count }) => ({ date, count }))

      const favoriteModules = Array.from(favoriteModulesMap.entries())
        .map(([module, count]) => ({ module, count }))
        .sort((a, b) => b.count - a.count)

      const preferredTimeSlots = Array.from(preferredTimeSlotsMap.entries())
        .map(([timeSlot, count]) => ({ timeSlot, count }))
        .sort((a, b) => b.count - a.count)

      const usersByCity = Array.from(usersByCityMap.values())
        .map(({ label, count }) => ({ city: label, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 15)

      const usersByCompanyMap = new Map<
        string,
        { ativo: number; inativo: number; semDados: number; cadastroParcial: number }
      >()
      for (const user of usersRows) {
        const company = user.company?.trim()
        if (!company) continue
        const snap = latestByUserId.get(user.user_id)
        const behavior = snap
          ? {
              is_active: Boolean(snap.comportamento?.is_active),
              risk_churn: Boolean(snap.comportamento?.risk_churn)
            }
          : undefined
        const status = resolveSuperappUserListStatus(user, behavior)
        const cur = usersByCompanyMap.get(company) || {
          ativo: 0,
          inativo: 0,
          semDados: 0,
          cadastroParcial: 0
        }
        switch (status) {
          case 'ativo':
            cur.ativo++
            break
          case 'inativo':
            cur.inativo++
            break
          case 'sem_dados':
            cur.semDados++
            break
          case 'cadastro_parcial':
            cur.cadastroParcial++
            break
        }
        usersByCompanyMap.set(company, cur)
      }
      const topCompaniesByUsers = Array.from(usersByCompanyMap.entries())
        .map(([company, stats]) => ({
          company,
          ativo: stats.ativo,
          inativo: stats.inativo,
          semDados: stats.semDados,
          cadastroParcial: stats.cadastroParcial,
          count: stats.ativo + stats.inativo + stats.semDados + stats.cadastroParcial
        }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 20)

      const lastActionTop = Array.from(lastActionMap.entries())
        .map(([action, count]) => ({ action, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 12)

      const tu = totalUsers || 0
      let noEmailCount = 0
      let noPhoneCount = 0
      let noLocationCount = 0

      for (const row of usersRows) {
        if (!row.email?.trim()) noEmailCount++
        if (!row.phone?.trim()) noPhoneCount++
        const snap = latestByUserId.get(row.user_id)
        if (!snap || !hasLocation(snap)) noLocationCount++
      }

      const pct = (n: number) => (tu > 0 ? (n / tu) * 100 : 0)

      const cadastroQuality = {
        noEmailCount,
        noEmailPercent: pct(noEmailCount),
        noPhoneCount,
        noPhonePercent: pct(noPhoneCount),
        noLocationCount,
        noLocationPercent: pct(noLocationCount)
      }

      const indicationByCpf = await superappService.getAppIndicationByCpfMap()
      let usersIndicacao = 0
      let usersSegurado = 0
      for (const user of usersRows) {
        const digits = normalizeCpfDigits(user.cpf)
        if (digits && indicationByCpf[digits]) usersIndicacao++
        else usersSegurado++
      }

      const lifecycleEngagement = [
        {
          bucket: 'Novos (≤30 dias)',
          avgScore: lifeEng['Novos (≤30 dias)'].count > 0 ? lifeEng['Novos (≤30 dias)'].sum / lifeEng['Novos (≤30 dias)'].count : 0,
          count: lifecycleNew
        },
        {
          bucket: 'Em desenvolvimento (31–90)',
          avgScore:
            lifeEng['Em desenvolvimento (31–90)'].count > 0
              ? lifeEng['Em desenvolvimento (31–90)'].sum / lifeEng['Em desenvolvimento (31–90)'].count
              : 0,
          count: lifecycleDeveloping
        },
        {
          bucket: 'Maduros (90+ dias)',
          avgScore: lifeEng['Maduros (90+ dias)'].count > 0 ? lifeEng['Maduros (90+ dias)'].sum / lifeEng['Maduros (90+ dias)'].count : 0,
          count: lifecycleMature
        }
      ]

      return {
        totalUsers: tu,
        totalBehaviors: totalBehaviors || 0,
        usersWithBehaviorRecords,
        usersWithBehaviorSnapshot,
        newUsersSinceLaunch20260513,
        avgNewUsersPerDaySinceLaunch20260513,
        usersSemDadosCadastroSinceLaunch20260513,
        usersCadastroParcialSinceLaunch20260513,
        activeUsersSinceLaunch20260513,
        usersSemDadosCadastro: usersSemDadosCount,
        usersCadastroParcial: usersCadastroParcialCount,
        activeUsers,
        usersByTipo: { indicacao: usersIndicacao, segurado: usersSegurado },
        atRiskUsers,
        churnRiskRatePercent,
        avgEngagementScore,
        behaviorsByDate,
        lastAccessByDate,
        assistanceActivationsByDate,
        usersByStatus,
        favoriteModules,
        preferredTimeSlots,
        usersByCity,
        topCompaniesByUsers,
        engagementBands: { low: engagementLow, medium: engagementMedium, high: engagementHigh },
        avgDaysSinceLastActivity,
        avgCustomerLifetimeDays,
        ghostUsersCount,
        preferredTimeSlotNullCount,
        lastActionTop,
        cadastroQuality,
        lifecycleBuckets: {
          newUsers: lifecycleNew,
          developing: lifecycleDeveloping,
          mature: lifecycleMature
        },
        lifecycleEngagement,
        riskProfile
      }
    } catch (error) {
      console.error('Erro ao buscar métricas:', error)
      throw error
    }
  },

  // Obter todas as promoções com filtros opcionais
  async getPromotions(filters: SuperappPromotionFilters = {}): Promise<SuperappPromotion[]> {
    try {
      return await fetchAllPages<SuperappPromotion>((from, to) => {
        let query = supabase.from('superapp_promotions').select('*')

        if (filters.title) {
          query = query.ilike('title', `%${filters.title}%`)
        }
        if (filters.establishment_name) {
          query = query.ilike('establishment_name', `%${filters.establishment_name}%`)
        }
        if (filters.type) {
          query = query.ilike('type', `%${filters.type}%`)
        }
        if (filters.start_date) {
          query = query.gte('start_date', filters.start_date)
        }
        if (filters.end_date) {
          query = query.lte('end_date', filters.end_date)
        }

        return query.order('start_date', { ascending: false }).range(from, to)
      })
    } catch (error) {
      console.error('Erro ao buscar promoções:', error)
      throw error
    }
  },

  async getDashboardMilestones(): Promise<SuperappDashboardMilestone[]> {
    try {
      const { data, error } = await supabase
        .from('superapp_dashboard_milestones')
        .select('*')
        .order('event_date', { ascending: true })

      if (error) throw error
      return data || []
    } catch (error) {
      console.error('Erro ao buscar marcos do dashboard:', error)
      throw error
    }
  },

  async createDashboardMilestone(
    payload: SuperappDashboardMilestonePayload
  ): Promise<SuperappDashboardMilestone> {
    try {
      const { data, error } = await supabase
        .from('superapp_dashboard_milestones')
        .insert({
          name: payload.name.trim(),
          event_date: payload.event_date,
          updated_at: new Date().toISOString(),
        })
        .select('*')
        .single()

      if (error) throw error
      await logMilestoneCreate(data)
      return data
    } catch (error) {
      console.error('Erro ao criar marco do dashboard:', error)
      throw error
    }
  },

  async updateDashboardMilestone(
    id: string,
    payload: SuperappDashboardMilestonePayload
  ): Promise<SuperappDashboardMilestone> {
    try {
      const { data: previous, error: fetchError } = await supabase
        .from('superapp_dashboard_milestones')
        .select('name, event_date')
        .eq('id', id)
        .single()

      if (fetchError) throw fetchError

      const { data, error } = await supabase
        .from('superapp_dashboard_milestones')
        .update({
          name: payload.name.trim(),
          event_date: payload.event_date,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .select('*')
        .single()

      if (error) throw error
      await logMilestoneUpdate(previous, data)
      return data
    } catch (error) {
      console.error('Erro ao atualizar marco do dashboard:', error)
      throw error
    }
  },

  async deleteDashboardMilestone(id: string): Promise<void> {
    try {
      const { data: previous, error: fetchError } = await supabase
        .from('superapp_dashboard_milestones')
        .select('*')
        .eq('id', id)
        .single()

      if (fetchError) throw fetchError

      const { error } = await supabase
        .from('superapp_dashboard_milestones')
        .delete()
        .eq('id', id)

      if (error) throw error
      await logMilestoneDelete(previous)
    } catch (error) {
      console.error('Erro ao excluir marco do dashboard:', error)
      throw error
    }
  },
}
